import React from 'react'
import { NEVER, Observable, of } from 'rxjs'
import { map, filter, first as rxFirst } from 'rxjs/operators'
import { first, last, mapValues, range, result, startsWith, keys, includes, find, isArray } from 'libraries/lodash'
import Route from 'route-parser'
import { RESPONSE_PENDING, RESPONSE_BADRESPONSE } from 'front-end-core/constants/responses'
import { LEG_SELECTION, LEG_COMMENTS, LEGISLATION_TAKEOVER_CODE } from 'constants/feature-flags'
import { KEY_PICKS, EMPLOYMENT_LEGISLATION_KEY_PICKS } from 'constants/legislation'
import { accordionGroup, accordionContent } from 'helpers/accordion'
import { observePageState, updatePageState } from 'front-end-core/services/PageStateService'
import { toSectionHtml } from 'front-end-core/helpers/xml'
import { isValidResponse, mapResponse } from 'front-end-core/helpers/dataflow'
import url from 'front-end-core/core/url'
import { LEGISLATION } from 'constants/controllers'
import { observeSelected, toggleContent } from 'front-end-core/services/StateService'
import { requestComments } from 'services/CommentsService'
import observeRequest from 'helpers/fcObserveRequest'
import { toast } from 'react-toastify'
import notificationToast from 'helpers/notificationToast'
import LinkIcon from '@material-ui/icons/Link'

import {
  URL_TAKEOVER_CODE_COPY,
  URL_TAKEOVER_CODE_PUBLISH,
  URL_TAKEOVER_CODE_EDIT_TITLE,
  URL_TAKEOVER_CODE_EDIT_PARAGRAPH,
  URL_TAKEOVER_CODE_DELETE_PARAGRAPH,
  URL_TAKEOVER_CODE_CREATE_PARAGRAPH,
  URL_TAKEOVER_CODE_EDIT_PREFIX,
  TAKEOVER_INTERNAL
} from 'constants/urls'
import {
  LEGISLATION_ACT,
  LEGISLATION_ACTS,
  LEGISLATION_FOLDERS,
  TAKEOVER_CODE,
  TAKEOVER_CODE_VERSIONS
} from 'constants/requests'
import { fetch } from 'services/ApiService'
import { USER_TAKEOVER_EDITOR, USER_TAKEOVER_PUBLISHER } from 'constants/user-roles'
import IconButton from '@material-ui/core/IconButton'
import TealToolTip from 'components/TealToolTip'
import reduxStore from 'redux/store'

const copyTakeoverRoute = new Route(URL_TAKEOVER_CODE_COPY)
const publishTakeoverRoute = new Route(URL_TAKEOVER_CODE_PUBLISH)
const editTakeoverSectionTitleRoute = new Route(URL_TAKEOVER_CODE_EDIT_TITLE)
const editTakeoverParagraphRoute = new Route(URL_TAKEOVER_CODE_EDIT_PARAGRAPH)
const deleteTakeoverParagraphRoute = new Route(URL_TAKEOVER_CODE_DELETE_PARAGRAPH)
const createTakeoverParagraphRoute = new Route(URL_TAKEOVER_CODE_CREATE_PARAGRAPH)
const editTakeoverPrefixRoute = new Route(URL_TAKEOVER_CODE_EDIT_PREFIX)

export const requestActs = function (aol) {
  return new Observable(observer =>
    fetch(LEGISLATION_ACTS, { aol }).subscribe(observer)
  )
}

export const requestAct = function ({ act, versionId = null, aol }) {
  ;[act] = act.split(':')
  return new Observable(observer =>
    fetch(act === TAKEOVER_INTERNAL ? TAKEOVER_CODE : LEGISLATION_ACT, {
      act,
      versionId,
      aol
    }).subscribe(observer)
  )
}

export const requestFolders = function (aol) {
  return new Observable(observer =>
    fetch(LEGISLATION_FOLDERS, { aol }).subscribe(observer)
  )
}

export const requestActVersions = function (act) {
  if (act !== TAKEOVER_INTERNAL) {
    return of(false)
  }
  return new Observable(observer =>
    fetch(TAKEOVER_CODE_VERSIONS).subscribe(observer)
  )
}

// TODO can remove ?
/* const extractNode = function (nodes, leaf) {
  if (!leaf) {
    return null
  }

  const fromPath = function (nodes, path) {
    const id = path[0]
    const node = find(nodes, n => n.id === id)

    if (path.length > 1) {
      return Object.assign({ displayed: false }, node, {
        descendants: [fromPath(node.descendants, path.slice(1))]
      })
    }

    return Object.assign({ displayed: true }, node)
  }
  const content = fromPath(nodes, leaf.path)
  return Object.assign({ content }, leaf)
} */

// TODO can remove ?
/* export const requestLeafParent = function (data) {
  const { act, path } = data
  return requestAct({ act })
    .map(act => {
      const leafIndex = findIndex(
        act.leafParents,
        leaf => leaf.pathStr === path
      )
      if (leafIndex < 0) {
        throw new Error('Content not found')
      }
      const content = [...act.parts, ...act.schedules]
      const previous = extractNode(content, act.leafParents[leafIndex - 1])
      const current = extractNode(content, act.leafParents[leafIndex])
      const next = extractNode(content, act.leafParents[leafIndex + 1])
      return Object.assign(
        { previous, current, next },
        pick(act, ['id', 'title', 'date', 'excerpt'])
      )
    })
    .catch(e => Observable.of(RESPONSE_BADDATA))
} */

export const extractPath = function (path, nodes) {
  if (path.length === 0) {
    return nodes
  }
  const [id, ...tail] = path
  return extractPath(tail, find(nodes, node => node.id === id).descendants)
}

export const toPath = function (itemPath) {
  if (
    itemPath.length === 2 &&
    (itemPath[1] === 'part' || itemPath[1] === 'schedule')
  ) {
    return itemPath.join('/')
  }

  if (last(itemPath) === 'content') {
    return itemPath.slice(-2).join('/')
  }

  return last(itemPath)
}

const $alwaysExpanded = of({ expanded: 1 })

const idxFilter = x => !!x

const isTopLevel = node => node.path.length <= 1

const toItemId = (aol, node) => url(LEGISLATION, { aol, path: toPath(node.path) })

const $selected = node => {
  return !isTopLevel(node) && LEG_SELECTION
    ? observeSelected('legislation', node.leaves).pipe(
      map(r => ({
        selected: r.reduce((a, i) => a && i.selected, 1)
      }))
    )
    : NEVER
}

const $expanded = node => {
  const parentPath = node.path.slice(0, -1)
  return isTopLevel(node)
    ? $alwaysExpanded
    : node.isLeaf
      ? observePageState(toPath([...parentPath, 'content'])).pipe(
        map(expanded => ({
          expanded
        }))
      )
      : observePageState(toPath(node.path)).pipe(map(expanded => ({ expanded })))
}

const $comments = node => {
  const commentTarget = first(node.path).replace(/:/g, '')
  const commentReference = node.id
  return LEG_COMMENTS
    ? requestComments(commentTarget, commentReference).pipe(
      map(comments => ({
        comments,
        commentTarget,
        commentCategory: 'legislation',
        commentReference
      }))
    )
    : NEVER
}

const getContentNodeSuffix = (aol, node) => (
  <TealToolTip
    arrow
    title='Copy (unique section) link to clipboard'
  >
    <a
      href={null}
      data-clipboard-text={url(LEGISLATION, { aol, path: toPath(node.path) }, {}, true)}
      onClick={() => {
        navigator.clipboard.writeText(url(LEGISLATION, { aol, path: toPath(node.path) }, {}, true))
        notificationToast(<LinkIcon />, toast.TYPE.INFO, 'Link copied to clipboard')
      }}
      className='pointer'
    >
      <IconButton size='small'>
        <LinkIcon />
      </IconButton>
    </a>
  </TealToolTip>
)

const getGroupNodePrefix = node =>
  !isTopLevel(node) && !!node.level && !!node.number
    ? `${node.level} ${node.number}`
    : ''

const getGroupNodeSuffix = node => {
  const min = findLimitNumber(first, node)
  const max = findLimitNumber(last, node)
  return isTopLevel(node)
    ? ''
    : min && max && min !== max ? `${min} - ${max}` : min || max || ''
}

const mapDescendantId = (node, filterIds, versionId = null) => id =>
  mapDescendant(node.getNode(id), filterIds, versionId)

const mapDescendant = (node, filterIds, versionId = null) => {
  const reduxState = reduxStore.getState()
  const aol = reduxState.aol.name

  return node.isLeaf
    ? mapDescendantContent(aol, node, !!filterIds)
    : mapDescendantGroup(aol, node, filterIds, versionId)
}

const mapDescendantGroup = (aol, node, filterIds, versionId = null) => (
  accordionGroup({
    id: toItemId(aol, node),
    originalNode: node.originalNode,
    isArchived: !!node.isArchived,
    title: node.title,
    path: node.path,
    prefix: getGroupNodePrefix(node),
    suffix: getGroupNodeSuffix(node),
    groupSource: $legislationGroup(node.id, filterIds, versionId, aol),
    stateSource: filterIds
      ? [$alwaysExpanded]
      : [$expanded(node), $selected(node)]
  })
)

const mapDescendantContent = (aol, node, reviewMode) => (
  accordionContent({
    id: toItemId(aol, node),
    originalNode: node.originalNode,
    title: node.title,
    prefix: node.number && `${node.number}`,
    suffix: getContentNodeSuffix(aol, node),
    path: node.path,
    content: toSectionHtml(node.content, 'span'),
    stateSource: reviewMode
      ? [$alwaysExpanded, $comments(node)]
      : [$expanded(node), $selected(node), $comments(node)]
  })
)

const nodePathFilter = (node, filterIds) => id => {
  const paths =
    filterIds &&
    filterIds
      .map(id => node.getNode(id).path)
      .reduce((a, p) => [...a, ...p], [])
  return filterIds ? paths.indexOf(id) > -1 : true
}

const mapNodeDescendants = (
  nodes,
  rootId,
  nodeId,
  filterIds,
  versionId = null
) => {
  const node = Object.prototype.hasOwnProperty.call(nodes, nodeId) ? nodes[nodeId] : null
  const grouped = groupNodeDescendants(node, filterIds)

  return !node
    ? [mapDescendant(nodes[rootId], filterIds, versionId)]
    : node.hasParts || node.hasSchedules || node.hasCrossheadings
      ? [
          node.hasParts && grouped('part', 'parts', 'Parts'),
          node.hasCrossheadings && grouped('crossheading', 'crossheadings', 'Crossheadings'),
          node.hasSchedules && grouped('schedule', 'schedules', 'Schedules')
        ].filter(idxFilter)
      : node.descendants
        .filter(nodePathFilter(node, filterIds))
        .map(mapDescendantId(node, filterIds, versionId))
}

const groupNodeDescendants = (node, filterIds) => (groupLevel, id, title) => {
  const idFilter = nodePathFilter(node, filterIds)
  const filter = id => idFilter(id) && node.getNode(id).level === groupLevel
  const sourceContent = node.descendants
    .filter(filter)
    .map(mapDescendantId(node, filterIds))
  const $source = of(sourceContent)

  return sourceContent.length
    ? accordionGroup({
      id,
      title,
      path: [...node.path, id],
      groupSource: $source,
      stateSource: $alwaysExpanded
    })
    : null
}

export const $legislationGroup = (act, filterIds = null, versionId = null, aol) =>
  requestAct({ act, versionId, aol }).pipe(
    map(
      mapResponse(({ root, nodes }) =>
        mapNodeDescendants(nodes, root, act, filterIds, versionId)
      )
    )
  )

const findLimitNumber = (take, node) => {
  if (!node) {
    return null
  }
  if (node.content) {
    return node.number || null
  }
  const descendantId = take(node.descendants)
  const descendant = node.getNode(descendantId)

  return findLimitNumber(take, descendant)
}

export const expandLegislationPath = (nodeId, aol) => {
  const pathRange = path =>
    range(1, path.length + 1).map(i => toPath(path.slice(0, i)))

  return requestAct({ act: nodeId, aol })
    .pipe(
      filter(resp => resp !== RESPONSE_PENDING),
      map(
        mapResponse(resp => {
          const path = result(
            resp,
            ['nodes', nodeId, 'path'],
            resp.nodes[resp.root].path
          )
          const [actId, groupId, ...tail] = path
          const group = result(resp, ['nodes', groupId, 'level'])
          const act = result(resp, ['nodes', actId])
          const itemPath =
            groupId && act.hasParts && act.hasSchedules
              ? [actId, group, groupId, ...tail]
              : path
          updatePageState(
            Object.assign(
              {
                [toPath([actId, 'part'])]: 1,
                [toPath([actId, 'schedule'])]: 1,
                [toPath([...itemPath.slice(0, -1), 'content'])]: 1
              },
              ...pathRange(itemPath).map(p => ({ [p]: 1 }))
            )
          )
          return itemPath
        })
      )
    )
}

export const expandCallback = (path, type, _, value) => {
  path = path.split('/')
  const id = last(path)

  if (type === 'content') {
    return (
      updatePageState(state => ({
        [toPath([...path.slice(0, -1), 'content'])]: value
      })) || true
    )
  }

  updatePageState(s => {
    if (value !== 0) {
      s = mapValues(s, (v, p) => (startsWith(p, id) ? -1 : v))
    }

    return { ...s, [toPath(path)]: value }
  })

  return false
}

export const selectCallback = (path, type, _, value, aol) => {
  let id, act
  switch (type) {
    case 'content':
      id = path.split('/').pop()
      toggleContent(value, 'legislation', id)
      return true
    case 'group':
      path = path.split('/')
      act = first(path)
      id = last(path)
      requestAct({ act, aol }).pipe(
        filter(isValidResponse),
        rxFirst(),
        map(x => x.nodes[id])
      ).subscribe(node => {
        toggleContent(value, 'legislation', node.leaves)
      })
      return true
    default:
      console.error('unknown type in selectCallback')
      break
  }
}

const createRequest = ({ route, method, paramKeys }) => {
  return params => route === copyTakeoverRoute && observeRequest({
    url: route.reverse(),
    headers: { 'Content-type': 'application/json' },
    method,
    body: JSON.stringify(pluck(params, paramKeys))
  }).pipe(
    map(response => ({
      success: response !== RESPONSE_BADRESPONSE,
      response
    }))
  )
}

export const createTakeoverVersion = createRequest({
  route: copyTakeoverRoute, method: 'post', paramKeys: ['id', 'notes']
})

export const publishTakeoverVersion = createRequest({
  route: publishTakeoverRoute, method: 'put', paramKeys: ['id']
})

export const editTakeoverSectionTitle = createRequest({
  route: editTakeoverSectionTitleRoute, method: 'post', paramKeys: ['id', 'title']
})

export const editTakeoverParagraph = createRequest({
  route: editTakeoverParagraphRoute, method: 'post', paramKeys: ['id', 'indentLevel', 'text']
})

export const deleteTakeoverParagraph = params => observeRequest({
  url: deleteTakeoverParagraphRoute.reverse({ paragraphId: params.id }),
  headers: { 'Content-type': 'application/json' },
  method: 'delete'
}).pipe(
  map(response => ({
    success: response !== RESPONSE_BADRESPONSE,
    response
  }))
)

export const createTakeoverParagraph = createRequest({
  route: createTakeoverParagraphRoute, method: 'post', paramKeys: ['sectionId', 'text', 'indentLevel', 'indexNumber']
})

export const editTakeoverPrefix = createRequest({
  route: editTakeoverPrefixRoute, method: 'post', paramKeys: ['id', 'text']
})

export const editableActsMap = {
  [TAKEOVER_INTERNAL]: { editor: USER_TAKEOVER_EDITOR, publisher: USER_TAKEOVER_PUBLISHER }
}

const pluck = (obj, allowed) =>
  keys(obj).filter(
    key => includes(allowed, key)
  ).reduce((acc, key) => {
    acc[key] = obj[key]
    return acc
  }, {})

const getDefaultKeyPicks = (aol) => {
  switch (aol) {
    case 'corporate':
      return KEY_PICKS
    case 'employment':
      return EMPLOYMENT_LEGISLATION_KEY_PICKS
    default:
      return KEY_PICKS
  }
}

export const requestLegislationKeyPicks = (aol) => requestActs(aol).pipe(
  map(
    response => isArray(response)
      ? response.filter(item => LEGISLATION_TAKEOVER_CODE || item.id !== TAKEOVER_INTERNAL)
      : response
  ),
  map(
    response =>
      isArray(response)
        ? response.filter(item => getDefaultKeyPicks(aol).reduce((containsKey, key) => item.id === key || containsKey, false))
        : response
  )
)
